/**
 * API Configuration and Bot Protection
 * 
 * To change the API endpoints:
 * 1. Update DEMO_FORM_URL below to your backend endpoint for demo requests
 * 2. Update CONTACT_FORM_URL below to your backend endpoint for contact requests
 * 
 * Both endpoints should:
 * - Accept POST requests with Content-Type: application/json
 * - Accept a 'token' field in the request body (reCAPTCHA v3 or similar)
 * - Handle gracefully and return success/error responses
 * 
 * Example response format:
 * { success: true, message: "Form submitted successfully" }
 * { success: false, error: "reCAPTCHA verification failed" }
 */

// Bot Protection Configuration
export const BOT_PROTECTION = {
  // Set to true to enable reCAPTCHA v3
  enabled: true,
  
  // Get your reCAPTCHA v3 site key from https://www.google.com/recaptcha/admin
  // This is the PUBLIC key - safe to expose in frontend code
  siteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY || '',
  
  // reCAPTCHA v3 threshold (0.0 to 1.0)
  // 0.0 = almost certainly a bot
  // 1.0 = almost certainly human
  threshold: 0.5
}

// API Endpoints Configuration
export const API_ENDPOINTS = {
  // Demo form submission endpoint
  // POST endpoint that should handle demo request submissions
  demoForm: import.meta.env.VITE_DEMO_API_URL || 'https://beta.tzimer360.co.il/api/hook/bookonline',
  
  // Contact form submission endpoint
  // POST endpoint that should handle contact requests
  contactForm: import.meta.env.VITE_CONTACT_API_URL || 'https://beta.tzimer360.co.il/api/hook/bookonline'
}

/**
 * How to set up Bot Protection (reCAPTCHA v3):
 * 
 * 1. Create a .env.local file in the root of your project:
 *    VITE_RECAPTCHA_SITE_KEY=your_site_key_here
 *    VITE_DEMO_API_URL=https://your-backend.com/api/demo
 *    VITE_CONTACT_API_URL=https://your-backend.com/api/contact
 * 
 * 2. Get your reCAPTCHA keys from: https://www.google.com/recaptcha/admin
 *    - Choose reCAPTCHA v3
 *    - Add your domain
 *    - Copy the site key (public) and secret key (private for backend)
 * 
 * 3. On your backend:
 *    - Receive the token from the frontend POST request
 *    - Verify it using Google's verification endpoint: 
 *      https://www.google.com/recaptcha/api/siteverify
 *    - Use the secret key (keep private on backend)
 *    - Check score >= 0.5 to accept the form
 * 
 * 4. Backend Verification Example (Node.js/Express):
 * 
 *    app.post('/api/demo', async (req, res) => {
 *      const { token, name, email, company, marketplaces, message } = req.body;
 *      
 *      // Verify reCAPTCHA token
 *      const verifyUrl = 'https://www.google.com/recaptcha/api/siteverify';
 *      const response = await fetch(verifyUrl, {
 *        method: 'POST',
 *        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
 *        body: `secret=${RECAPTCHA_SECRET_KEY}&response=${token}`
 *      });
 *      
 *      const data = await response.json();
 *      
 *      if (!data.success || data.score < 0.5) {
 *        return res.status(400).json({ success: false, error: 'Bot protection failed' });
 *      }
 *      
 *      // Process form submission
 *      // Save to database, send email, etc.
 *      
 *      res.json({ success: true, message: 'Submitted successfully' });
 *    });
 */
